import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as authService from '../../services/auth';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  /** --------------------
   * Auth0
   --------------------- */
  const {
    loginWithRedirect,
    logout: auth0Logout,
    isAuthenticated: isAuth0Authenticated,
    getAccessTokenSilently,
    isLoading: auth0Loading,
  } = useAuth0();

  /** --------------------
   * State
   --------------------- */
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  /** --------------------
   * Init storage
   --------------------- */
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /** --------------------
   * Login email
   --------------------- */
  const loginWithEmail = useCallback(async ({ email, password, remember }) => {
    const response = await authService.login({
      email,
      password,
      remember
    });

    const {
      accessToken,
      token: legacyToken,
      user: responseUser,
    } = response.data;

    const finalToken = accessToken || legacyToken;

    setToken(finalToken);
    setUser(responseUser);

    localStorage.setItem('accessToken', finalToken);
    localStorage.setItem('user', JSON.stringify(responseUser));

    return responseUser;
  }, []);

  /** --------------------
   * Google/Auth0 login
   --------------------- */
  const loginWithGoogle = useCallback(() => {
    return loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email',
      },
    });
  }, [loginWithRedirect]);

  /** --------------------
   * Apple login
   --------------------- */
  const loginWithApple = useCallback(() => {
    return loginWithRedirect({
      authorizationParams: {
        connection: 'apple',
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email',
      },
    });
  }, [loginWithRedirect]);

  /** --------------------
   * Complete Auth0 login
   --------------------- */
  const completeAuth0Login = useCallback(async () => {
    try {
      const auth0Token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      if (!auth0Token) return;

      const response = await authService.loginWithAuth0(auth0Token);
      const { accessToken, client: responseUser } = response.data;

      setToken(accessToken);
      setUser(responseUser);

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('user', JSON.stringify(responseUser));
    } catch (error) {
      console.error('Error completing Auth0 login:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    }
  }, [getAccessTokenSilently]);

  /** --------------------
   * Detect Auth0 redirect
   --------------------- */
  useEffect(() => {
    if (isAuth0Authenticated && !token) {
      completeAuth0Login();
    }
  }, [isAuth0Authenticated, token, completeAuth0Login]);

  /** --------------------
   * Logout
   --------------------- */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    }
  }, [auth0Logout]);

  /** --------------------
   * Forgot
   --------------------- */
  const forgot = useCallback(async (payload) => {
    const response = await authService.forgot(payload);
    return response.data?.message;
  }, []);

  /** --------------------
   * Computed
   --------------------- */
  const isAuthenticated = useMemo(() => !!token, [token]);
  const finalLoading = loading || auth0Loading;

  /** --------------------
   * Context value
   --------------------- */
  const value = useMemo(
    () => ({
      user,
      token,
      loading: finalLoading,
      isAuthenticated,
      loginWithEmail,
      loginWithGoogle,
      loginWithApple,
      logout,
      forgot,
    }),
    [
      user,
      token,
      finalLoading,
      isAuthenticated,
      loginWithEmail,
      loginWithGoogle,
      loginWithApple,
      logout,
      forgot,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
